/**
 * Encrypted local storage (AES-GCM via WebCrypto) over IndexedDB.
 * - A non-extractable AES-256 key is generated once per device and stored in
 *   IndexedDB — it never leaves this browser profile (device binding).
 * - `getDeviceId()` returns a stable per-device UUID sent as `X-Device-Id`
 *   so the backend can bind + remotely revoke devices.
 * - `wipeOfflineData()` clears every encrypted record (used on revoke).
 */
import { get, set, del, keys } from "idb-keyval";

const DEVICE_ID_KEY = "am_device_id";
const KEY_SLOT = "am_sec_key_v1";

export function getDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

let keyPromise = null;
function getKey() {
  if (!keyPromise) {
    keyPromise = (async () => {
      let k = await get(KEY_SLOT);
      if (!k) {
        k = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
        await set(KEY_SLOT, k); // CryptoKey is structured-clone friendly; non-extractable
      }
      return k;
    })();
  }
  return keyPromise;
}

const te = new TextEncoder();
const td = new TextDecoder();

export async function secureSet(name, value) {
  const key = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, te.encode(JSON.stringify(value)));
  await set(name, { iv, ct });
}

export async function secureGet(name) {
  try {
    const rec = await get(name);
    if (!rec || !rec.ct) return undefined;
    const key = await getKey();
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: rec.iv }, key, rec.ct);
    return JSON.parse(td.decode(pt));
  } catch (e) {
    console.debug("secureGet failed", name, e);
    return undefined;
  }
}

export async function secureDel(name) {
  try { await del(name); } catch (e) { console.debug(e); }
}

/** Wipe ALL locally stored offline data (queue, caches, crypto key). */
export async function wipeOfflineData() {
  try {
    const all = await keys();
    await Promise.all(
      all
        .filter((k) => typeof k === "string" && k.startsWith("am_"))
        .map((k) => del(k))
    );
  } catch (e) { console.debug(e); }
  keyPromise = null;
  localStorage.removeItem("am_new_reg_draft");
}
